import { z } from 'zod';
export const FX_SCALE = 1_000_000;
/** Trusts the caller. Use at boundaries where the unit is already known. */
export const usdCents = (n) => n;
export const audCents = (n) => n;
export const fxMicro = (n) => n;
/**
 * Integer division rounding half away from zero — the same direction as
 * `Math.round` for positives, and symmetric for negatives so a sign flip cannot
 * change the magnitude of a result.
 */
export function divRound(numerator, denominator) {
    if (denominator === 0)
        throw new RangeError('divRound: denominator is zero');
    const q = numerator / denominator;
    return q < 0 ? -Math.round(-q) : Math.round(q);
}
/**
 * Converts a decimal money string or number to integer cents.
 *
 * Deliberately does not use `parseFloat`: `12.34` parsed as a float and
 * multiplied by 100 is `1233.9999999999998`. Splitting on the decimal point and
 * padding is exact.
 *
 * Returns `null` for anything that is not a finite, non-negative amount —
 * blank, whitespace, NaN, Infinity, or a negative. `null` means "unknown", which
 * every gate treats as a fail. See code_practise.md §6.
 */
export function toCents(value) {
    if (value === null || value === undefined)
        return null;
    const text = typeof value === 'number' ? String(value) : value.trim();
    if (text === '')
        return null;
    const match = /^(\d+)(?:\.(\d{0,}))?$/.exec(text);
    if (!match)
        return null;
    const whole = match[1] ?? '0';
    const fraction = (match[2] ?? '').padEnd(2, '0');
    // More than two decimal places: round the third onward rather than truncate.
    const centsFromFraction = fraction.length <= 2
        ? Number(fraction.slice(0, 2))
        : divRound(Number(fraction), 10 ** (fraction.length - 2));
    const cents = Number(whole) * 100 + centsFromFraction;
    return Number.isSafeInteger(cents) ? cents : null;
}
/** Converts AUD cents to USD cents. `fx` is AUD per 1 USD. */
export function audToUsd(amount, fx) {
    return usdCents(divRound(amount * FX_SCALE, fx));
}
/** Converts USD cents to AUD cents. */
export function usdToAud(amount, fx) {
    return audCents(divRound(amount * fx, FX_SCALE));
}
/**
 * `have >= total * pct / 100`, tested by cross-multiplication so there is no
 * division and therefore no rounding error at the boundary. `pct` may carry up
 * to three decimal places, matching the database column.
 */
export function meetsPercentage(have, total, pct) {
    const pctMilli = Math.round(pct * 1000);
    return have * 100_000 >= total * pctMilli;
}
/**
 * A ratio as a percentage with three decimal places of precision, or `null` when
 * the denominator is zero. Never returns NaN or Infinity — code_practise.md §5.
 */
export function percentageOf(part, whole) {
    if (whole === 0)
        return null;
    return divRound(part * 100_000, whole) / 1000;
}
/** Formats USD cents for display, e.g. `$35,000`. Whole dollars, en-AU grouping. */
export function formatMoney(cents) {
    if (cents === null || cents === undefined || !Number.isFinite(cents))
        return '—';
    return '$' + Math.round(cents / 100).toLocaleString('en-AU');
}
/** Formats a count with en-AU grouping. */
export function formatCount(n) {
    return n.toLocaleString('en-AU');
}
/**
 * Formats a percentage. Matches the legacy rule of showing two decimals below
 * 0.1% so a very small share does not render as a flat `0.0%`.
 */
export function formatPercent(pct) {
    if (pct === null || pct === undefined || !Number.isFinite(pct))
        return '—';
    return (pct < 0.1 ? pct.toFixed(2) : pct.toFixed(1)) + '%';
}
// --- Zod primitives ---------------------------------------------------------
/** A non-negative integer cent amount, capped to keep a typo from overflowing. */
export const CentsSchema = z
    .number()
    .int()
    .min(0)
    .max(1_000_000_000_000, 'Amount is implausibly large');
/**
 * FX rate in micro-units. Bounded well outside any plausible AUD/USD rate so a
 * fat-fingered value is rejected rather than silently distorting every payout.
 * `0` means unset, which makes AUD figures display as `—`.
 */
export const FxMicroSchema = z
    .number()
    .int()
    .min(0)
    .max(100 * FX_SCALE)
    .refine((v) => v === 0 || v >= FX_SCALE / 100, 'FX rate is implausibly small');
//# sourceMappingURL=money.js.map